import React from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import PartnerProfileEditor from '../components/Succeedo/PartnerProfile/PartnerProfileEditor';
import AppLayout from '../components/Succeedo/Layout/AppLayout';

// Pulls every editable field on the partner's CoachProfile so the form
// can pre-fill. Same shape the partner saw at signup
// (CreatePartnerProfile) plus headline + needs_onboarding.
const GET_MY_COACH_PROFILE = gql`
  query GetMyCoachProfile {
    currentUser {
      id
      name
      coach_profile {
        id
        name
        headline
        bio
        avatar_url
        org_name
        is_partner
        partner_category
        geography_regions
        industry_tags
        contact_email
        website_url
        phone
        accepts_introductions
      }
    }
  }
`;

const UPDATE_COACH_PROFILE = gql`
  mutation UpdateCoachProfile(
    $name: String,
    $headline: String,
    $bio: String,
    $orgName: String,
    $partnerCategory: String,
    $geographyRegions: [String!],
    $industryTags: [String!],
    $contactEmail: String,
    $websiteUrl: String,
    $phone: String,
    $acceptsIntroductions: Boolean
  ) {
    update_coach_profile(
      name: $name,
      headline: $headline,
      bio: $bio,
      org_name: $orgName,
      partner_category: $partnerCategory,
      geography_regions: $geographyRegions,
      industry_tags: $industryTags,
      contact_email: $contactEmail,
      website_url: $websiteUrl,
      phone: $phone,
      accepts_introductions: $acceptsIntroductions
    ) {
      id
      name
      headline
      bio
      avatar_url
      org_name
      partner_category
      geography_regions
      industry_tags
      contact_email
      website_url
      phone
      accepts_introductions
    }
  }
`;

const UPLOAD_COACH_AVATAR = gql`
  mutation UploadCoachAvatar($file: File!) {
    upload_coach_avatar(file: $file) {
      id
      avatar_url
    }
  }
`;

function PartnerProfileEditorWithData() {
  const { data, loading: queryLoading, refetch } = useQuery(GET_MY_COACH_PROFILE, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateProfile, { loading: mutationLoading }] = useMutation(UPDATE_COACH_PROFILE);
  const [uploadAvatar] = useMutation(UPLOAD_COACH_AVATAR);

  const coachProfile = data?.currentUser?.coach_profile || null;

  const handleSave = async (variables) => {
    const { data: result, errors: gqlErrors } = await updateProfile({ variables });
    if (gqlErrors?.length) throw new Error(gqlErrors.map((e) => e.message).join(', '));
    return result?.update_coach_profile;
  };

  const handleUploadAvatar = async (file) => {
    const { data: result, errors: gqlErrors } = await uploadAvatar({
      variables: { file },
      context: { hasUpload: true },
    });
    if (gqlErrors?.length) throw new Error(gqlErrors.map((e) => e.message).join(', '));
    await refetch();
    return result?.upload_coach_avatar;
  };

  if (queryLoading && !coachProfile) {
    return (
      <AppLayout breadcrumb="EDIT PROFILE">
        <div className="flex items-center justify-center p-12">
          <div className="text-muted-foreground text-[16px]">Loading profile…</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb="EDIT PROFILE">
      <PartnerProfileEditor
        coachProfile={coachProfile}
        onSave={handleSave}
        onUploadAvatar={handleUploadAvatar}
        loading={mutationLoading}
      />
    </AppLayout>
  );
}

const SucceedoPartnerProfileEditorContainer = () => (
  <ApolloProvider client={client}>
    <PartnerProfileEditorWithData />
  </ApolloProvider>
);

export default SucceedoPartnerProfileEditorContainer;
